import * as yup from "yup";

import { FormDataType } from "../components/FormDataTypes";
import { LANDLORD_QUOTE_UNAVAILABLE_MESSAGE, getIsLandlordQuoteUnavailable } from "../landlord/hooks/useEffectiveDate";
import { getAllowedReplacementCostPercentages, getAllowedValuationKeys } from "../landlord/rules";
import { QuoteLocation } from "../types/Rater.types";

function getEffectiveDateSchema() {
  return yup
    .string()
    .required()
    .test(
      "effective-date-availability",
      LANDLORD_QUOTE_UNAVAILABLE_MESSAGE,
      (effectiveDate) => !getIsLandlordQuoteUnavailable(effectiveDate)
    )
    .label("Effective Date");
}

function getFormFieldsSchema(activeFormsData: Record<string, FormDataType>) {
  return yup.object().when("name", (name: string, schema: yup.AnyObjectSchema) => {
    const formData = activeFormsData[name];

    if (!formData?.yupShape) {
      return schema;
    }

    return schema.concat(formData.yupShape);
  });
}

function getSelectedFormSchema(activeFormsData: Record<string, FormDataType>) {
  return yup.object().shape({
    name: yup
      .string()
      .oneOf(Object.keys(activeFormsData), ({ value }) => `${value} is not available for this effective date`)
      .required(),
    fields: getFormFieldsSchema(activeFormsData),
  });
}

const REQUIRED_OCCUPANCY_FORMS: Record<string, string> = {
  S: "SLP01",
  V: "VAC01",
  STR: "STR01",
};

const CONDITIONAL_AUTOMATED_FORMS = ["SLP01", "VAC01", "STR01"];

function getFormDisplayName(activeFormsData: Record<string, FormDataType>, formName: string) {
  const formData = activeFormsData[formName];
  return formData?.displayFormName ?? formData?.formTitle ?? formName;
}

function getRequiredNewLocationForms(location: QuoteLocation) {
  const requiredForms = ["FL373A"];
  const requiredOccupancyForm = REQUIRED_OCCUPANCY_FORMS[location.occupancyType];

  if (requiredOccupancyForm) {
    requiredForms.push(requiredOccupancyForm);
  }

  return requiredForms;
}

function getNewLocationFormsSchema(activeFormsData: Record<string, FormDataType>) {
  return yup
    .array()
    .of(getSelectedFormSchema(activeFormsData))
    .test("required-new-business-forms", "Required forms are missing", function (forms) {
      const location = this.parent as QuoteLocation;
      const formNames = (forms ?? []).map((form) => form.name).filter((formName): formName is string => !!formName);
      const missingForms = getRequiredNewLocationForms(location).filter((formName) => !formNames.includes(formName));

      if (missingForms.length === 0) return true;

      return this.createError({
        message: `${missingForms
          .map((formName) => getFormDisplayName(activeFormsData, formName))
          .join(", ")} must be attached for this quote`,
      });
    })
    .test("stale-automated-forms", "Automated forms are no longer valid", function (forms) {
      const location = this.parent as QuoteLocation;
      const requiredForms = getRequiredNewLocationForms(location);
      const staleForms = (forms ?? [])
        .map((form) => form.name)
        .filter((formName): formName is string => !!formName)
        .filter((formName) => CONDITIONAL_AUTOMATED_FORMS.includes(formName) && !requiredForms.includes(formName));

      if (staleForms.length === 0) return true;

      return this.createError({
        message: `${staleForms
          .map((formName) => getFormDisplayName(activeFormsData, formName))
          .join(", ")} must be removed for this quote`,
      });
    });
}

export function getNewLPSchema(activeFormsData: Record<string, FormDataType>) {
  return yup
    .object({
      isBusinessName: yup
        .string()
        .oneOf(["personal", "business"], () => `Must be business or personal name`)
        .required()
        .label("Line"),
      businessName: yup
        .string()
        .trim()
        .when("isBusinessName", (isBusinessName: string, schema: yup.StringSchema) => {
          if (isBusinessName === "business") {
            return schema.max(64, "Business name cannot exceed 64 characters").required();
          }
          return schema;
        })
        .label("Business Name"),

      firstName: yup
        .string()
        .trim()
        .when("isBusinessName", (isBusinessName: string, schema: yup.StringSchema) => {
          if (isBusinessName === "personal") {
            return schema.max(64, "First name cannot exceed 64 characters").required();
          }
          return schema;
        })
        .label("First name"),
      lastName: yup
        .string()
        .trim()
        .when("isBusinessName", (isBusinessName: string, schema: yup.StringSchema) => {
          if (isBusinessName === "personal") {
            return schema.max(64, "Last name cannot exceed 64 characters").required();
          }
          return schema;
        })
        .label("Last name"),
      streetAddress: yup.string().trim().required().label("Street address"),
      streetAddress2: yup.string().trim().label("Street address 2"),
      city: yup.string().required().label("City"),
      state: yup.string().required().min(2).max(2).label("State"),
      zip: yup.string().required().label("Zip"),
      effectiveDate: getEffectiveDateSchema(),
      forms: yup.array().of(getSelectedFormSchema(activeFormsData)),
      liabilityForm: yup
        .string()
        .oneOf(["None", "FLOLT"], (field) => `${field.label} must be selected from the options`)
        .required()
        .label("Liability Form"),
      liability: yup
        .number()
        .when(
          ["liabilityForm", "locations"],
          // @ts-ignore
          (liabilityForm: any, locations: QuoteLocation[], schema: yup.NumberSchema) => {
            if (liabilityForm !== "None") {
              if (locations.some((item) => item.valuation === "ACV" || item.valuation === "IACV")) {
                return schema
                  .oneOf([100000, 300000, 500000], () => `All locations must be RC for this option`)
                  .required();
              } else {
                return schema
                  .oneOf(
                    [100000, 300000, 500000, 1000000],
                    (field) => `${field.label} must be selected from the options`
                  )
                  .required();
              }
            }

            return schema;
          }
        )
        .label("Liability"),
      medpay: yup
        .number()
        .when("liabilityForm", (liabilityForm: string, schema: yup.NumberSchema) => {
          if (liabilityForm !== "None") {
            return schema.oneOf([5000], (field) => `${field.label} must be selected from the options`).required();
          }
          return schema;
        })
        .label("Medpay"),
      locations: yup.array().of(
        yup.object().shape({
          streetAddress: yup.string().required().label("Street address"),
          city: yup.string().required().label("City"),
          state: yup.string().required().label("State"),
          zip: yup
            .number()
            .positive()
            .integer()
            .typeError((field) => `${field.label} is a required field`)
            .required()
            .label("Zip"),
          constructionType: yup
            .string()
            .oneOf(["F", "M"], (field) => `${field.label} must be Frame or Masonry`)
            .typeError((field) => `${field.label} must be selected from the options`)
            .required()
            .label("Construction Type"),
          occupancyType: yup
            .string()
            .transform((value) => (value === "" ? undefined : value))
            .oneOf(
              ["T", "S", "V", "STR"],
              ({ label }) => `${label} must be Tenant, Seasonal, Vacant, or Short-Term Rental`
            )
            .required()
            .label("Occupancy Type"),
          protectionClass: yup
            .string()
            .typeError((field) => `${field.label} must be selected from the options`)
            .required()
            .label("Protection Class"),
          yearBuilt: yup
            .number()
            .min(1776)
            .max(2100)
            .typeError((field) => `${field.label} must be a number`)
            .required()
            .label("Year built"),
          numFamilies: yup
            .number()
            .oneOf([1, 2, 3, 4], (field) => `${field.label} must be between 1 and 4`)
            .typeError((field) => `${field.label} must be a number`)
            .required()
            .label("Number of Families"),
          valuation: yup
            .string()
            .when("perils", (perils: string, schema: yup.StringSchema) => {
              const allowedValuations = getAllowedValuationKeys(perils, true);
              return schema.oneOf(
                allowedValuations,
                ({ label }) => `${label} must be selected from the options available for this peril`
              );
            })
            .typeError((field) => `${field.label} must be selected from the options`)
            .required()
            .label("Valuation"),
          RCpercentage: yup
            .number()
            .when("valuation", (valuation: string, schema: yup.NumberSchema) => {
              const allowedPercentages = getAllowedReplacementCostPercentages(valuation, true);
              if (allowedPercentages.length === 0) return schema;

              const formattedPercentages = allowedPercentages.map((percentage) => `${percentage}%`).join(" or ");
              const valuationLabel = valuation === "IACV" ? "Indexed Actual Cash Value" : "Replacement Cost";

              return schema.oneOf(
                allowedPercentages,
                (field) => `${field.label} must be ${formattedPercentages} with ${valuationLabel}`
              );
            })
            .typeError((field) => `${field.label} must be selected from the options`)
            .label("RC Percentage"),
          perils: yup
            .string()
            .when("occupancyType", (occupancyType: string, schema: yup.StringSchema) => {
              if (occupancyType === "V") {
                return schema.oneOf(["FL1R"], "Perils must be FL-1R when Occupancy Type is Vacant");
              }

              return schema.oneOf(
                ["FL1R", "FL1RV", "FL2", "FL3"],
                (field) => `${field.label} must be selected from the options`
              );
            })
            .typeError((field) => `${field.label} must be selected from the options`)
            .required()
            .label("Perils"),
          deductible: yup
            .number()
            .typeError((field) => `${field.label} must be selected from the options`)
            .required()
            .label("Deductible"),
          RCcovA: yup.number().when("valuation", (valuation: string, schema: yup.NumberSchema) => {
            if (valuation === "RC" || valuation === "IACV") {
              return schema
                .typeError((field) => `${field.label} must be a number`)
                .required()
                .label("RC Coverage A");
            }

            return schema.label("RC Coverage A");
          }),
          ACVestimate: yup.number().label("ACV Estimate"),
          covA: yup
            .number()
            .when("perils", (peril: string, schema: yup.NumberSchema) => {
              switch (peril) {
                case "FL1R":
                case "FL1RV":
                  return schema.min(30000, `Minimum Coverage A for forms FL-1R or FL-1RV is $30,000`);
                case "FL2":
                  return schema.when("valuation", (valuation: string, schema: yup.NumberSchema) => {
                    if (valuation === "ACV") return schema.min(50000, `Minimum Coverage A for form FL-2 is $50,000`);
                    else if (valuation === "RC")
                      return schema.min(100000, `Minimum Coverage A for form FL-2 is $100,000`);
                    else return schema;
                  });

                case "FL3":
                  return schema.min(100000, `Minimum Coverage A for form FL-3 is $100,000`);
                default:
                  return schema;
              }
            })
            .min(20000, (field) => `${field.label} must be greater than $20,000`)
            .integer()
            .typeError((field) => `${field.label} must be a number`)
            .required()
            .label("Coverage A"),
          covB: yup
            .number()
            .min(0)
            .integer()
            .typeError((field) => `${field.label} must be a number`)
            .required()
            .label("Coverage B"),
          covC: yup
            .number()
            .min(0)
            .integer()
            .typeError((field) => `${field.label} must be a number`)
            .required()
            .label("Coverage C"),
          covD: yup
            .number()
            .min(0)
            .integer()
            .typeError((field) => `${field.label} must be a number`)
            .required()
            .label("Coverage D"),

          forms: getNewLocationFormsSchema(activeFormsData),
        })
      ),
    })
    .required();
}
export function getOldLPSchema(activeFormsData: Record<string, FormDataType>) {
  return yup
    .object({
      isBusinessName: yup
        .string()
        .oneOf(["personal", "business"], () => `Must be business or personal name`)
        .required()
        .label("Line"),
      businessName: yup
        .string()
        .trim()
        .when("isBusinessName", (isBusinessName: string, schema: yup.StringSchema) => {
          if (isBusinessName === "business") {
            return schema.max(64, "Business name cannot exceed 64 characters").required();
          }
          return schema;
        })
        .label("Business Name"),

      firstName: yup
        .string()
        .trim()
        .when("isBusinessName", (isBusinessName: string, schema: yup.StringSchema) => {
          if (isBusinessName === "personal") {
            return schema.max(64, "First name cannot exceed 64 characters").required();
          }
          return schema;
        })
        .label("First name"),
      lastName: yup
        .string()
        .trim()
        .when("isBusinessName", (isBusinessName: string, schema: yup.StringSchema) => {
          if (isBusinessName === "personal") {
            return schema.max(64, "Last name cannot exceed 64 characters").required();
          }
          return schema;
        })
        .label("Last name"),
      streetAddress: yup.string().trim().required().label("Street address"),
      streetAddress2: yup.string().trim().label("Street address 2"),
      city: yup.string().required().label("City"),
      state: yup.string().required().min(2).max(2).label("State"),
      zip: yup.string().required().label("Zip"),
      effectiveDate: getEffectiveDateSchema(),
      forms: yup.array().of(getSelectedFormSchema(activeFormsData)),
      liabilityForm: yup
        .string()
        .oneOf(["None", "FLOLT"], (field) => `${field.label} must be selected from the options`)
        .required()
        .label("Liability Form"),
      liability: yup
        .number()
        .when(
          ["liabilityForm", "locations"],
          // @ts-ignore
          (liabilityForm: any, locations: QuoteLocation[], schema: yup.NumberSchema) => {
            if (liabilityForm !== "None") {
              if (locations.some((item) => item.valuation === "ACV")) {
                return schema
                  .oneOf([100000, 300000, 500000], () => `All locations must be RC for this option`)
                  .required();
              } else {
                return schema
                  .oneOf(
                    [100000, 300000, 500000, 1000000],
                    (field) => `${field.label} must be selected from the options`
                  )
                  .required();
              }
            }

            return schema;
          }
        )
        .label("Liability"),
      medpay: yup
        .number()
        .when("liabilityForm", (liabilityForm: string, schema: yup.NumberSchema) => {
          if (liabilityForm !== "None") {
            return schema.oneOf([5000], (field) => `${field.label} must be selected from the options`).required();
          }
          return schema;
        })
        .label("Medpay"),
      locations: yup.array().of(
        yup.object().shape({
          streetAddress: yup.string().required().label("Street address"),
          city: yup.string().required().label("City"),
          state: yup.string().required().label("State"),
          zip: yup
            .number()
            .positive()
            .integer()
            .typeError((field) => `${field.label} is a required field`)
            .required()
            .label("Zip"),
          constructionType: yup
            .string()
            .oneOf(["F", "M"], (field) => `${field.label} must be Frame or Masonry`)
            .typeError((field) => `${field.label} must be selected from the options`)
            .required()
            .label("Construction Type"),
          protectionClass: yup
            .string()
            .typeError((field) => `${field.label} must be selected from the options`)
            .required()
            .label("Protection Class"),
          yearBuilt: yup
            .number()
            .min(1776)
            .max(2100)
            .typeError((field) => `${field.label} must be a number`)
            .required()
            .label("Year built"),
          numFamilies: yup
            .number()
            .oneOf([1, 2, 3, 4], (field) => `${field.label} must be between 1 and 4`)
            .typeError((field) => `${field.label} must be a number`)
            .required()
            .label("Number of Families"),
          valuation: yup
            .string()
            .when("perils", (perils: string, schema: yup.StringSchema) => {
              const allowedValuations = getAllowedValuationKeys(perils, false);
              return schema.oneOf(
                allowedValuations,
                ({ label }) => `${label} must be selected from the options available for this peril`
              );
            })
            .typeError((field) => `${field.label} must be selected from the options`)
            .required()
            .label("Valuation"),
          RCpercentage: yup
            .number()
            .typeError((field) => `${field.label} must be selected from the options`)
            .label("RC Percentage"),
          perils: yup
            .string()
            .oneOf(["FL1R", "FL1RV", "FL2", "FL3"], (field) => `${field.label} must be selected from the options`)
            .typeError((field) => `${field.label} must be selected from the options`)
            .required()
            .label("Perils"),
          deductible: yup
            .number()
            .typeError((field) => `${field.label} must be selected from the options`)
            .required()
            .label("Deductible"),
          RCcovA: yup.number().label("RC Coverage A"),
          covA: yup
            .number()
            .min(20000, (field) => `${field.label} must be greater than $20,000`)
            .integer()
            .typeError((field) => `${field.label} must be a number`)
            .required()
            .label("Coverage A"),
          covB: yup
            .number()
            .min(0)
            .integer()
            .typeError((field) => `${field.label} must be a number`)
            .required()
            .label("Coverage B"),
          covC: yup
            .number()
            .min(0)
            .integer()
            .typeError((field) => `${field.label} must be a number`)
            .required()
            .label("Coverage C"),
          covD: yup
            .number()
            .min(0)
            .integer()
            .typeError((field) => `${field.label} must be a number`)
            .required()
            .label("Coverage D"),

          forms: yup.array().of(getSelectedFormSchema(activeFormsData)),
        })
      ),
    })
    .required();
}
